import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useDevMode } from '@/contexts/DevModeContext';

export interface TryOnLookItem {
  id: string;
  name: string;
  image: string;
  price?: number | null;
  currency?: string;
  store?: string | null;
  url?: string | null;
}

export interface TryOnLook {
  id: string;
  user_id: string;
  result_image_url: string;
  avatar_type: string;
  items: TryOnLookItem[];
  created_at: string;
  animated_video_url?: string | null;
  animation_status?: string | null;
  animation_error?: string | null;
  pinned_at?: string | null;
}

interface UseTryOnLooksOptions {
  page?: number;
  pageSize?: number;
  sortOrder?: 'newest' | 'oldest';
}

export function useTryOnLooks(options: UseTryOnLooksOptions = {}) {
  const { page = 1, pageSize = 12, sortOrder = 'newest' } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isDevMode } = useDevMode();

  // Fetch paginated looks - sorted by pinned first, then by date
  const { data: looksData, isLoading, error, refetch } = useQuery({
    queryKey: ['tryon-looks', user?.id, page, pageSize, sortOrder],
    queryFn: async () => {
      if (!user) return { looks: [], totalCount: 0 };

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Order by pinned_at first (nulls last), then by created_at
      const { data, error, count } = await supabase
        .from('tryon_looks')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('pinned_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: sortOrder === 'oldest' })
        .range(from, to);

      if (error) throw error;

      const looks: TryOnLook[] = (data || []).map(row => ({
        ...row,
        items: (Array.isArray(row.items) ? row.items : []) as unknown as TryOnLookItem[],
      }));

      return { looks, totalCount: count || 0 };
    },
    enabled: !!user,
  });

  const looks = looksData?.looks || [];
  const totalCount = looksData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const saveLook = useMutation({
    mutationFn: async (look: {
      resultImageUrl: string;
      avatarType: string;
      items: { id: string; name: string; image: string }[];
    }) => {
      if (!user) throw new Error('Not authenticated');

      // First, upload the base64 image to storage
      let imageUrl = look.resultImageUrl;
      
      if (look.resultImageUrl.startsWith('data:')) {
        // Convert base64 to blob
        const response = await fetch(look.resultImageUrl);
        const blob = await response.blob();
        
        const fileName = `${user.id}/looks/${Date.now()}.jpg`;
        
        const { error: uploadError } = await supabase.storage
          .from('saved-images')
          .upload(fileName, blob, { contentType: 'image/jpeg' });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('saved-images')
          .getPublicUrl(fileName);

        imageUrl = publicUrl;
      }

      const { data, error } = await supabase
        .from('tryon_looks')
        .insert({
          user_id: user.id,
          result_image_url: imageUrl,
          avatar_type: look.avatarType,
          items: look.items,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tryon-looks', user?.id] });
    },
  });

  const deleteLook = useMutation({
    mutationFn: async (lookId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('tryon_looks')
        .delete()
        .eq('id', lookId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tryon-looks', user?.id] });
    },
  });

  const togglePin = useMutation({
    mutationFn: async ({ lookId, isPinned }: { lookId: string; isPinned: boolean }) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('tryon_looks')
        .update({ pinned_at: isPinned ? null : new Date().toISOString() } as any)
        .eq('id', lookId);

      if (error) throw error;
    },
    onMutate: async ({ lookId, isPinned }) => {
      await queryClient.cancelQueries({ queryKey: ['tryon-looks', user?.id] });
      const previousData = queryClient.getQueriesData({ queryKey: ['tryon-looks', user?.id] });

      queryClient.setQueriesData<{ looks: TryOnLook[]; totalCount: number }>(
        { queryKey: ['tryon-looks', user?.id] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            looks: old.looks.map((look) =>
              look.id === lookId
                ? { ...look, pinned_at: isPinned ? null : new Date().toISOString() }
                : look
            ),
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tryon-looks', user?.id] });
    },
  });

  return {
    looks: isDevMode ? [] : looks,
    totalCount: isDevMode ? 0 : totalCount,
    totalPages: isDevMode ? 0 : totalPages,
    isLoading,
    error,
    refetch,
    saveLook,
    deleteLook,
    togglePin,
  };
}
